import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, SendIcon, ShieldIcon, CreditCardIcon, TrendingUpIcon, CheckCircleIcon, AlertTriangleIcon, GiftIcon, ClockIcon, Image as ImageIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
type Category = 'Activity' | 'Transactions' | 'Request to Pay' | 'Marketing' | 'Others';
interface Notification {
  id: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  message: string;
  time: string;
  unread: boolean;
  category: Category;
}
const NOTIFICATIONS: Notification[] = [{
  id: '1',
  icon: SendIcon,
  iconColor: '#22C55E',
  iconBg: '#22C55E15',
  title: 'Transfer Successful',
  message: 'Rs 5,000 sent to Asad Ali via Bank Alfalah',
  time: '2 min ago',
  unread: true,
  category: 'Transactions'
}, {
  id: '2',
  icon: ShieldIcon,
  iconColor: '#F37024',
  iconBg: '#F3702415',
  title: 'Security Alert',
  message: 'New login detected from Lahore. Was this you?',
  time: '15 min ago',
  unread: true,
  category: 'Activity'
}, {
  id: '3',
  icon: CreditCardIcon,
  iconColor: '#661806',
  iconBg: '#66180615',
  title: 'Bill Due Reminder',
  message: 'Your electricity bill of Rs 3,200 is due tomorrow',
  time: '1 hour ago',
  unread: true,
  category: 'Transactions'
}, {
  id: '8',
  icon: ClockIcon,
  iconColor: '#EAB308',
  iconBg: '#EAB30815',
  title: 'Payment Request',
  message: 'Rashid Khan requested Rs 2,500 for Dinner split',
  time: '2 hours ago',
  unread: true,
  category: 'Request to Pay'
}, {
  id: '4',
  icon: GiftIcon,
  iconColor: '#8B5CF6',
  iconBg: '#8B5CF615',
  title: 'Cashback Earned!',
  message: 'You earned Rs 150 cashback on your last transaction',
  time: '3 hours ago',
  unread: false,
  category: 'Marketing'
}, {
  id: '9',
  icon: ImageIcon,
  iconColor: '#EC4899',
  iconBg: '#EC489915',
  title: 'New Feature: Split Bill',
  message: 'Now you can easily split bills with friends directly from the app!',
  time: '4 hours ago',
  unread: false,
  category: 'Marketing'
}, {
  id: '5',
  icon: TrendingUpIcon,
  iconColor: '#3B82F6',
  iconBg: '#3B82F615',
  title: 'Savings Goal Update',
  message: 'You\'re 75% towards your "Emergency Fund" goal',
  time: '5 hours ago',
  unread: false,
  category: 'Activity'
}, {
  id: '6',
  icon: CheckCircleIcon,
  iconColor: '#22C55E',
  iconBg: '#22C55E15',
  title: 'Payment Received',
  message: 'Rs 25,000 received from Rashid Khan',
  time: 'Yesterday',
  unread: false,
  category: 'Transactions'
}, {
  id: '7',
  icon: AlertTriangleIcon,
  iconColor: '#EAB308',
  iconBg: '#EAB30815',
  title: 'Low Balance Alert',
  message: 'Your account balance is below Rs 5,000',
  time: 'Yesterday',
  unread: false,
  category: 'Others'
}];
interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}
export function NotificationPanel({
  isOpen,
  onClose
}: NotificationPanelProps) {
  const {
    isDark
  } = useTheme();
  const [activeCategory, setActiveCategory] = useState<Category | 'All'>('All');
  const categories: (Category | 'All')[] = ['All', 'Activity', 'Transactions', 'Request to Pay', 'Marketing', 'Others'];
  const filteredNotifications = NOTIFICATIONS.filter(n => activeCategory === 'All' || n.category === activeCategory);
  const unreadCount = NOTIFICATIONS.filter(n => n.unread).length;
  return <AnimatePresence data-id="element-1607" data-mp="2hbdvlf08u1lz">
      {isOpen && <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className={`fixed inset-0 z-50 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-1608" data-mp="2hcf69idckm36">
          {/* Header */}
          <div className={`px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 z-10 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-1609" data-mp="23cw8nji5wt54">
            <div className="flex items-center gap-4" data-id="element-1610" data-mp="1k84n7xp21oa">
              <button onClick={onClose} className={`p-2 -ml-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1611" data-mp="1lj794fel3dt">
                <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-1612" data-mp="2vcple8ff466x" />
              </button>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1613" data-mp="2zchv4ogjsidv">
                Notifications
              </h1>
            </div>
            {unreadCount > 0 && <span className="bg-[#F37024] text-white text-[11px] font-bold px-2.5 py-1 rounded-full" data-id="element-1614" data-mp="2qc1xhvmhk5gx">
                {unreadCount} New
              </span>}
          </div>

          {/* Categories */}
          <div className="px-6 py-2" data-id="element-1615" data-mp="2gfytslbzjyrv">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2" data-id="element-1616" data-mp="2gh1mmysvcbaf">
              {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-[13px] font-bold whitespace-nowrap transition-all ${activeCategory === cat ? 'bg-[#F37024] text-white shadow-md' : isDark ? 'bg-[#2D1810] text-[#B8A89E] border border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#818285] border border-[#F0E8E3]'}`} data-id="element-1617" data-mp="22lfre9w1kv9k">
                  {cat}
                </button>)}
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-id="element-1618" data-mp="1a2n3ivdll3ym">
            {filteredNotifications.map(notification => {
          const Icon = notification.icon;
          return <div key={notification.id} className={`rounded-2xl p-4 flex gap-4 relative overflow-hidden transition-all ${notification.unread ? isDark ? 'bg-[#2D1810] border border-[#F37024]/30 shadow-sm' : 'bg-white border border-[#F37024]/20 shadow-sm' : isDark ? 'bg-[#1A0E08] border border-[rgba(243,112,36,0.1)] opacity-80' : 'bg-transparent border border-[#F0E8E3] opacity-70'}`} data-id="element-1619" data-mp="1j0p79iopuapl">
                  {notification.unread && <div className="absolute top-0 left-0 w-1 h-full bg-[#F37024]" data-id="element-1620" data-mp="21srtd9k4mj78" />}
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 mt-1" style={{
              backgroundColor: notification.iconBg
            }} data-id="element-1621" data-mp="2gqk5drqgspgo">
                    <Icon className="w-6 h-6" style={{
                color: notification.iconColor
              }} strokeWidth={2} data-id="element-1622" data-mp="2iu49s4tz1lf6" />
                  </div>
                  <div className="flex-1" data-id="element-1623" data-mp="2nqmb0uq3f1mr">
                    <div className="flex justify-between items-start mb-1" data-id="element-1624" data-mp="2srifpai9qrt8">
                      <h4 className={`text-[15px] font-bold pr-4 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1625" data-mp="2xi8lupyiqyoz">
                        {notification.title}
                      </h4>
                      <span className={`text-[11px] font-medium whitespace-nowrap ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-1626" data-mp="32jxwi4htxacj">
                        {notification.time}
                      </span>
                    </div>
                    <p className={`text-[13px] leading-relaxed mb-2 ${isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]'}`} data-id="element-1627" data-mp="3ghw8j38oguov">
                      {notification.message}
                    </p>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md" style={{
                backgroundColor: notification.iconBg,
                color: notification.iconColor
              }} data-id="element-1628" data-mp="1m33f9av9og03">
                      {notification.category}
                    </span>
                  </div>
                </div>;
        })}
            {filteredNotifications.length === 0 && <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-1629" data-mp="zv6cni0e0jo9">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-[#2D1810]' : 'bg-white'}`} data-id="element-1630" data-mp="1xiew77gtvinm">
                  <BellIcon className={`w-8 h-8 ${isDark ? 'text-[#B8A89E]/50' : 'text-[#818285]/50'}`} data-id="element-1631" data-mp="1skwvu5gvmbr8" />
                </div>
                <p className={`text-[15px] font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1632" data-mp="1nio39a7x1bpn">
                  No notifications
                </p>
                <p className={`text-[13px] mt-1 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-1633" data-mp="c5luh1c25qg1">
                  You're all caught up!
                </p>
              </div>}
          </div>
        </motion.div>}
    </AnimatePresence>;
}